import { useEffect, useState, type CSSProperties } from 'react'
import { cn } from './cn'

export interface SkeletonProps {
  /** Width in px or any CSS length. Defaults to `100%`. */
  width?: number | string
  /** Height in px or any CSS length. Defaults to `14px` (roughly one text line). */
  height?: number | string
  /** Renders a circle (avatars, chips). Overrides border-radius. */
  circle?: boolean
  /**
   * Ms to wait before rendering. TanStack Query often resolves in <150 ms; a
   * flash-guard delay keeps the skeleton from appearing on those fast loads.
   * Pass `0` to render immediately (e.g. inside an already-visible skeleton
   * group where the parent has already passed the delay).
   */
  delay?: number
  className?: string
  style?: CSSProperties
  /** Optional radius override (px or any CSS length). */
  radius?: number | string
}

/**
 * Loading placeholder that matches the final layout's shape. Night-Ops tinted,
 * with a subtle horizontal shimmer. Respects `prefers-reduced-motion` via the
 * `.skeleton` rule in index.css (animation becomes `none`, static tint remains).
 *
 * Usage rule of thumb:
 *   - Use skeletons for full sections that have no data yet (list, KPI row).
 *   - Use inline spinners / button "…" states for mutations, not skeletons.
 *   - Don't skeleton over data that's already visible (stale-while-revalidate).
 *
 * Composition presets — `SkeletonText`, `SkeletonAvatar`, `SkeletonCard`,
 * `SkeletonRow` — cover the common shapes. Reach for the base `Skeleton`
 * only when none fit.
 */
export function Skeleton({
  width = '100%',
  height = 14,
  circle,
  delay = 150,
  className,
  style,
  radius,
}: SkeletonProps) {
  const [show, setShow] = useState(delay === 0)
  useEffect(() => {
    if (delay === 0) return
    const id = window.setTimeout(() => setShow(true), delay)
    return () => window.clearTimeout(id)
  }, [delay])

  if (!show) return null

  return (
    <span
      aria-hidden="true"
      className={cn('skeleton', className)}
      style={{
        width,
        height,
        borderRadius: circle ? '50%' : (radius ?? 4),
        display: 'inline-block',
        ...style,
      }}
    />
  )
}

/* ──────────────────────────────────────────────────────────── presets ── */

export interface SkeletonTextProps {
  /** Number of lines. Default 1. */
  lines?: number
  /** Width of the last line as a fraction of full (helps mimic real text). Default 0.6. */
  lastLineWidth?: number
  delay?: number
  className?: string
}

/** N stacked text-line placeholders. Last line is shorter so it reads as paragraph-end. */
export function SkeletonText({
  lines = 1,
  lastLineWidth = 0.6,
  delay = 150,
  className,
}: SkeletonTextProps) {
  return (
    <span className={cn('skel-text', className)} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => {
        const isLast = i === lines - 1 && lines > 1
        return (
          <Skeleton
            key={i}
            height={12}
            width={isLast ? `${Math.round(lastLineWidth * 100)}%` : '100%'}
            delay={i === 0 ? delay : 0}
          />
        )
      })}
    </span>
  )
}

export interface SkeletonAvatarProps {
  /** Diameter in px. Default 32. */
  size?: number
  delay?: number
  className?: string
}

export function SkeletonAvatar({ size = 32, delay = 150, className }: SkeletonAvatarProps) {
  return (
    <Skeleton
      width={size}
      height={size}
      circle
      delay={delay}
      {...(className !== undefined ? { className } : {})}
    />
  )
}

export interface SkeletonCardProps {
  /** Card outer height in px. Default 96. */
  height?: number
  /** Inner padding in px. Default 14. */
  padding?: number
  /** Show a header row (avatar + 2 text lines). Default true. */
  withHeader?: boolean
  /** Number of body lines. Default 2. */
  bodyLines?: number
  delay?: number
  className?: string
}

/**
 * Card-shaped placeholder — outer panel border + inner header (avatar + 2
 * lines) + N body lines. Matches the silhouette of `Panel` so swap-in is clean.
 */
export function SkeletonCard({
  padding = 14,
  withHeader = true,
  bodyLines = 2,
  delay = 150,
  className,
}: SkeletonCardProps) {
  return (
    <div className={cn('skel-card', className)} aria-hidden="true" style={{ padding }}>
      {withHeader && (
        <div className="skel-card__head">
          <SkeletonAvatar size={32} delay={delay} />
          <div className="skel-card__head-text">
            <Skeleton height={11} width="55%" delay={delay} />
            <Skeleton height={9} width="40%" delay={0} />
          </div>
        </div>
      )}
      <div className="skel-card__body">
        <SkeletonText lines={bodyLines} delay={withHeader ? 0 : delay} />
      </div>
    </div>
  )
}

export interface SkeletonRowProps {
  /** Number of column placeholders. Default 4. */
  columns?: number
  /** Optional explicit column widths (CSS lengths). Length should match `columns`. */
  columnWidths?: (number | string)[]
  /** Row height in px. Default 38. */
  height?: number
  delay?: number
  className?: string
}

/** List-row placeholder — N evenly-spaced bars matching a typical data-table row. */
export function SkeletonRow({
  columns = 4,
  columnWidths,
  height = 38,
  delay = 150,
  className,
}: SkeletonRowProps) {
  const widths = columnWidths ?? Array.from({ length: columns }).map(() => '100%')
  return (
    <div
      className={cn('skel-row', className)}
      aria-hidden="true"
      style={{ height, gridTemplateColumns: widths.map((w) => (typeof w === 'number' ? `${w}px` : w)).join(' ') }}
    >
      {widths.map((_, i) => (
        <Skeleton key={i} height={11} width="80%" delay={i === 0 ? delay : 0} />
      ))}
    </div>
  )
}
